import * as React from 'react';
import clsx from 'clsx';
import { useSkeletonContext } from './SkeletonContext';

export const Skeleton = React.forwardRef(function Skeleton(
  props: React.PropsWithChildren<Props> | any,
  ref
) {
  const {
    animation = 'wave',
    component,
    height,
    variant = 'text',
    width,
    disabled: propDisabled,
    ...other
  } = props;

  const { isLoading } = useSkeletonContext();
  const disabled = propDisabled ?? !isLoading;
  const Component = component ?? (variant === 'text' ? 'span' : 'div');
  if (disabled) {
    return (other.children as React.ReactElement) || null;
  }

  const hasChildren = Boolean(other.children);

  return (
    <Component
      ref={ref}
      className={clsx(
        'fr-skeleton-2',
        hasChildren && 'with-children',
        hasChildren && !width && 'fit-content',
        hasChildren && !height && 'height-auto'
      )}
      data-type={variant}
      data-animation={animation}
      style={{
        width,
        height
      }}
      {...other}
    />
  );
});

export interface Props {
  /**
   * Hide Skeleton
   */
  disabled?: boolean;
  /**
   * The animation.
   * If `false` the animation effect is disabled.
   * @default 'wave'
   */
  animation?: 'pulse' | 'wave' | false;
  /**
   * Optional children to infer width and height from.
   */
  children?: React.ReactNode;
  /**
   * Height of the skeleton.
   * Useful when you don't want to adapt the skeleton to a text element but for instance a card.
   */
  height?: number | string;
  /**
   * The type of content that will be rendered.
   * @default 'text'
   */
  variant?: 'circular' | 'rectangular' | 'text';
  /**
   * Width of the skeleton.
   * Useful when the skeleton is inside an inline element with no width of its own.
   */
  width?: number | string;
  component?: React.ElementType;
}
